import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { FileData, FileDataAction } from './useFileData'
import Parties from '../../pages/Parties'
import EmailOverview from '../../pages/Email/EmailOverview'
import DocumentOverview from '../../pages/Document/DocumentOverview'
import Tasks from '../../pages/Tasks'
import { FileAction } from '../../config/fileConfig'
import Times from '../../pages/Times'
import DisbursementOverview from '../../pages/File/Registration/DisbursementOverview'
import { LegalAidCertificateOverview } from '../../pages/File/Registration/LegalAidCertificateOverview'
import TravelOverview from '../../pages/File/Registration/TravelOverview'
import Notes from '../../pages/Notes'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DocketProceedingOverview } from '../../pages/File/Registration/DocketProceedingOverview'
import FamilyLawOverview from '../../pages/File/Registration/FamilyLawOverview'
import InjuryOverview from '../../pages/File/Registration/InjuryOverview'

export type FilePageProps = {
    key: string
    dataActionKey?: string
    pivotItemKey?: string // Determines the active item in the pivot menu.
    route: string
    content: (file: FileData, action: FileDataAction) => ReactElement
}

export const fileRoutePrefix = '/file/:fileId'

const defaultPageKey = 'documents'

export default function useFilePages(fileId: string): {
    pages: FilePageProps[]
    activePage: FilePageProps
    getPageRoute: (route: string) => string
} {
    const dispatch = useDispatch()
    const location = useLocation()
    const pages = useMemo<FilePageProps[]>(
        () => [
            {
                key: 'parties',
                route: 'party',
                content: (file: FileData) => (
                    <Parties
                        prefix={fileRoutePrefix}
                        overviewProps={{
                            data: file.parties.data,
                            documents: file.documents.data,
                            file: file.current.data,
                        }}
                    />
                ),
            },
            {
                key: 'emails',
                route: 'email',
                content: (file: FileData, action: FileDataAction) => (
                    <EmailOverview
                        messages={file.emails}
                        documents={file.documents}
                        file={file.current}
                        reset={action}
                    />
                ),
            },
            {
                key: 'documents',
                route: 'document',
                content: (file: FileData, action: FileDataAction) => (
                    <DocumentOverview
                        documents={file.documents}
                        parties={file.parties.data}
                        file={file.current.data}
                        reset={action}
                    />
                ),
            },
            {
                key: 'tasks',
                route: 'task',
                content: (file: FileData) => (
                    <Tasks
                        routePrefix={fileRoutePrefix}
                        useFileContext
                        overviewProps={{
                            global: false,
                            data: file.tasks.data,
                            reset: () => dispatch({ type: FileAction.ResetTasks }),
                        }}
                    />
                ),
            },
            {
                key: 'times',
                pivotItemKey: 'registration',
                route: 'time',
                content: (file: FileData) => (
                    <Times
                        routePrefix={fileRoutePrefix}
                        useFileContext
                        overviewProps={{
                            global: false,
                            data: file.times.data,
                        }}
                    />
                ),
            },
            {
                key: 'disbursements',
                pivotItemKey: 'registration',
                route: 'disbursement',
                content: (file: FileData, action: FileDataAction) => (
                    <DisbursementOverview
                        listProps={{ file: file.current.data }}
                        items={file.disbursements.data}
                        reset={action}
                    />
                ),
            },
            {
                key: 'legalAidCertificates',
                pivotItemKey: 'registration',
                route: 'legal-aid-certificate',
                content: (file: FileData, action: FileDataAction) => (
                    <LegalAidCertificateOverview
                        file={file.current}
                        parties={file.parties}
                        certificates={file.legalAidCertificates}
                        reset={action}
                    />
                ),
            },
            {
                key: 'docketProceedings',
                pivotItemKey: 'registration',
                route: 'docket',
                content: (file: FileData, action: FileDataAction) => (
                    <DocketProceedingOverview
                        file={file.current}
                        parties={file.parties}
                        docketProceedings={file.docketProceedings}
                        reset={action}
                    />
                ),
            },
            {
                key: 'injury',
                pivotItemKey: 'registration',
                route: 'injury',
                content: () => <InjuryOverview />,
            },
            {
                key: 'travels',
                pivotItemKey: 'registration',
                route: 'travel',
                content: (file: FileData, action: FileDataAction) => (
                    <TravelOverview listProps={{ file: file.current.data }} items={file.travels.data} reset={action} />
                ),
            },
            {
                key: 'notes',
                route: 'note',
                content: (file: FileData) => (
                    <Notes overviewProps={{ notes: file.notes }} routePrefix={fileRoutePrefix} />
                ),
            },
            {
                key: 'personLaw',
                pivotItemKey: 'registration',
                route: 'person-law',
                content: (file: FileData) => <FamilyLawOverview file={file.current} />,
            },
        ],
        [fileId],
    )

    // By default, the item's key will be equal to the route, but it may be overridden by the route property.
    const getPageRoute = (route: string): string => (fileId && route ? `/file/${fileId}/${route}` : undefined)

    const activePage: FilePageProps = useMemo<FilePageProps>(() => {
        // The base route, e.g. /file/<UUID>/time
        const getBaseRoute = (page: FilePageProps) => getPageRoute(page.route)
        const activePageKey =
            pages.find(page => location.pathname.search(getBaseRoute(page)) === 0)?.key || defaultPageKey
        return pages.find(page => page.key === activePageKey)
    }, [location.pathname, fileId])

    return { pages, activePage, getPageRoute }
}
